import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AddPrayerNameModal from '../components/modals/AddPrayerNameModal';
import ClaimSuccessModal from '../components/modals/ClaimSuccessModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faHeart,
  faEllipsisV,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import groupService from '../api/groupService';

const PrayerListPage = () => {
  const { currentUser } = useAuth();
  const { adminGroup } = useGroup();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [prayerNames, setPrayerNames] = useState([]);
  const [showAddNameModal, setShowAddNameModal] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [nameToDelete, setNameToDelete] = useState(null);
  const [showClaimSuccessModal, setShowClaimSuccessModal] = useState(false);
  const [claimedNameRequest, setClaimedNameRequest] = useState(null);
  const dropdownRef = useRef(null);

  // Load prayer names
  useEffect(() => {
    if (adminGroup?.id) {
      loadPrayerNames();
    }
  }, [adminGroup?.id]);

  // Check if coming from claim flow (after prayer names are loaded)
  useEffect(() => {
    if (location.state?.showClaimSuccess && location.state?.claimCode) {
      fetchClaimedNameRequest(location.state.claimCode);
    }
  }, [location.state]);
  
  const fetchClaimedNameRequest = async (claimCode) => {
    try {
      // Get the name_request to show in modal
      const nameRequestResponse = await fetch(`/api/names/${claimCode}`);
      if (!nameRequestResponse.ok) return;
      
      const nameRequestData = await nameRequestResponse.json();
      if (!nameRequestData.success || !nameRequestData.nameRequest) return;
      
      // Show the modal with name_request data
      setClaimedNameRequest(nameRequestData.nameRequest);
      setShowClaimSuccessModal(true);
      
      // Clear location state to prevent modal from showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    } catch (error) {
      console.error('Error fetching claimed name request:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    
    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdownId]);

  // Load prayer names
  useEffect(() => {
    if (adminGroup?.id) {
      loadPrayerNames();
    }
  }, [adminGroup?.id]);

  const loadPrayerNames = async () => {
    if (!adminGroup?.id) return;
    
    try {
      setLoading(true);
      const response = await groupService.getPrayerNames(adminGroup.id);
      const names = response.data || [];
      
      console.log('📋 Loaded prayer names from API:', names.length, 'records');
      console.log('📋 First record:', names[0]);
      
      // Expand names that have JSON arrays in recipient_name
      const expandedNames = [];
      names.forEach(name => {
        try {
          // Check if recipient_name is a JSON array string
          if (name.recipient_name && name.recipient_name.trim().startsWith('[')) {
            const namesArray = JSON.parse(name.recipient_name);
            if (Array.isArray(namesArray)) {
              // Create a separate entry for each name in the array
              namesArray.forEach((individualName, index) => {
                expandedNames.push({
                  ...name,
                  id: `${name.id}_${index}`, // Unique ID for each expanded row
                  recipient_name: individualName,
                  _originalId: name.id, // Keep reference to original record
                  _isExpanded: true,
                  _expandedIndex: index
                });
              });
            } else {
              // Not an array, add as is
              expandedNames.push(name);
            }
          } else {
            // Not JSON, add as is
            expandedNames.push(name);
          }
        } catch (e) {
          // Not valid JSON, add as is
          expandedNames.push(name);
        }
      });
      
      console.log('📋 After expansion:', expandedNames.length, 'rows');
      
      // Deduplicate by prayer_group_id - keep only the first occurrence
      const seenPrayerGroupIds = new Set();
      const dedupedNames = expandedNames.filter(name => {
        // If no prayer_group_id, always include (admin-added names)
        if (!name.prayer_group_id) {
          return true;
        }
        
        // If prayer_group_id already seen, skip this record
        if (seenPrayerGroupIds.has(name.prayer_group_id)) {
          return false;
        }
        
        // First time seeing this prayer_group_id, include it and mark as seen
        seenPrayerGroupIds.add(name.prayer_group_id);
        return true;
      });
      
      console.log('📋 After deduplication:', dedupedNames.length, 'unique rows');
      setPrayerNames(dedupedNames);
    } catch (error) {
      console.error('Error loading prayer names:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddName = () => {
    setEditingName(null);
    setShowAddNameModal(true);
  };

  const handleEditName = (name) => {
    setOpenDropdownId(null);
    // Use original ID if this is an expanded row
    const nameToEdit = name._isExpanded ? { ...name, id: name._originalId } : name;
    setEditingName(nameToEdit);
    setShowAddNameModal(true);
  };

  const toggleDropdown = (nameId, event) => {
    if (openDropdownId === nameId) {
      setOpenDropdownId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        right: window.innerWidth - rect.right
      });
      setOpenDropdownId(nameId);
    }
  };

  const handleDeleteName = async (name) => {
    setOpenDropdownId(null);
    // Use original ID if this is an expanded row
    const idToDelete = name._isExpanded ? name._originalId : name.id;
    setNameToDelete(idToDelete);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!nameToDelete) return;
    
    try {
      await groupService.deletePrayerName(nameToDelete);
      // Remove all rows with matching original ID (handles expanded rows)
      setPrayerNames(prevNames => prevNames.filter(name => {
        const checkId = name._isExpanded ? name._originalId : name.id;
        return checkId !== nameToDelete;
      }));
      setShowDeleteModal(false);
      setNameToDelete(null);
      if (window.showToast) {
        window.showToast('Prayer name removed', 'success');
      }
    } catch (error) {
      console.error('Error deleting prayer name:', error);
      if (window.showToast) {
        window.showToast('Failed to delete prayer name', 'error');
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setNameToDelete(null);
  };

  const handleModalClose = () => {
    setShowAddNameModal(false);
    setEditingName(null);
  };

  const handleNameSaved = async (savedName) => {
    // Update state immediately without reloading
    if (editingName) {
      // Update existing name
      setPrayerNames(prevNames => 
        prevNames.map(name => name.id === savedName.id ? savedName : name)
      );
    } else {
      // Add new name to the top of the list
      setPrayerNames(prevNames => [savedName, ...prevNames]);
    }
    handleModalClose();
  };

  // Get intention display text
  const getIntentionDisplay = (name) => {
    if (name.intention_type === 'Custom' || !name.intention_type) {
      return name.custom_intention || 'Prayer';
    }
    return `For ${name.intention_type}`;
  };

  // Get intention badge color
  const getIntentionColor = (name) => {
    const type = name.intention_type;
    const colors = {
      'Refuah': '#ef4444',
      'Memory': '#6b7280',
      'Health': '#10b981',
      'Success': '#3b82f6',
      'Zivug': '#ec4899',
      'Custom': '#8b5cf6'
    };
    return colors[type] || '#6b7280';
  };

  // Use all prayer names without filtering
  const filteredNames = prayerNames;

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-semibold text-gray-900">Prayer List</h1>
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2"
              onClick={handleAddName}
            >
              Add Name
            </button>
          </div>

          {/* Prayer Names Table */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading prayer names...</p>
            </div>
          ) : filteredNames.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No prayer names yet. Add your first name to the prayer list.</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-visible mt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Intention</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Added By</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date Added</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredNames.map((name) => (
                      <tr key={name.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="font-medium text-gray-900">
                            {name.recipient_name}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span 
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                            style={{ 
                              color: getIntentionColor(name),
                              backgroundColor: `${getIntentionColor(name)}15`
                            }}
                          >
                            {getIntentionDisplay(name)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-900">
                            {name.submitter_first_name && name.submitter_last_name
                              ? `${name.submitter_first_name} ${name.submitter_last_name}`
                              : name.added_by_user?.full_name || 
                                name.added_by_user?.name || 
                                'Admin'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">
                            {new Date(name.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(name.created_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <button
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            onClick={(e) => toggleDropdown(name.id, e)}
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Dropdown Menu (rendered outside table) */}
          {openDropdownId && (
            <div 
              ref={dropdownRef}
              className="fixed w-32 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] py-1"
              style={{
                top: `${dropdownPosition.top}px`,
                right: `${dropdownPosition.right}px`
              }}
            >
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  const name = prayerNames.find(n => n.id === openDropdownId);
                  if (name) handleEditName(name);
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
                Edit
              </button>
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                onClick={() => {
                  const name = prayerNames.find(n => n.id === openDropdownId);
                  if (name) handleDeleteName(name);
                  setOpenDropdownId(null);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </button>
            </div>
          )}

          {/* Add/Edit Modal */}
          {showAddNameModal && (
            <AddPrayerNameModal
              editingName={editingName}
              onClose={handleModalClose}
              onSave={handleNameSaved}
            />
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]" onClick={cancelDelete}>
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Prayer Name</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this prayer name? This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Claim Success Modal */}
          {showClaimSuccessModal && claimedNameRequest && (
            <ClaimSuccessModal
              nameRequest={claimedNameRequest}
              onClose={() => setShowClaimSuccessModal(false)}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default PrayerListPage;
